/**
 * 균일 격자 공간 해시.
 *
 * 유닛이 수백 마리일 때 "가장 가까운 적"과 "서로 밀어내기"를 순진하게 짜면 O(N^2)다.
 * 카운팅 정렬로 셀별 인덱스 목록을 매 틱 새로 만든다. 할당 없음, 순서는 항상 인덱스 오름차순.
 * (Map/Set을 쓰면 순회 순서 때문에 결정론이 깨질 수 있으므로 쓰지 않는다.)
 */
export class SpatialHash {
  readonly cols: number;
  readonly rows: number;
  readonly cellSize: number;
  private readonly counts: Int32Array;
  private readonly starts: Int32Array;
  private readonly items: Int32Array;
  private readonly cursor: Int32Array;

  constructor(widthFp: number, heightFp: number, cellSizeFp: number, maxItems: number) {
    this.cellSize = cellSizeFp;
    this.cols = Math.max(1, Math.ceil(widthFp / cellSizeFp));
    this.rows = Math.max(1, Math.ceil(heightFp / cellSizeFp));
    const n = this.cols * this.rows;
    this.counts = new Int32Array(n);
    this.starts = new Int32Array(n + 1);
    this.cursor = new Int32Array(n);
    this.items = new Int32Array(maxItems);
  }

  private cellIndex(x: number, y: number): number {
    let cx = Math.floor(x / this.cellSize);
    let cy = Math.floor(y / this.cellSize);
    if (cx < 0) cx = 0;
    else if (cx >= this.cols) cx = this.cols - 1;
    if (cy < 0) cy = 0;
    else if (cy >= this.rows) cy = this.rows - 1;
    return cy * this.cols + cx;
  }

  /** alive[i]가 0이 아닌 유닛만 담는다. */
  build(count: number, xs: Int32Array, ys: Int32Array, alive: Uint8Array): void {
    this.counts.fill(0);
    for (let i = 0; i < count; i++) {
      if (alive[i] === 0) continue;
      this.counts[this.cellIndex(xs[i], ys[i])]++;
    }
    let acc = 0;
    for (let c = 0; c < this.counts.length; c++) {
      this.starts[c] = acc;
      this.cursor[c] = acc;
      acc += this.counts[c];
    }
    this.starts[this.counts.length] = acc;
    for (let i = 0; i < count; i++) {
      if (alive[i] === 0) continue;
      const c = this.cellIndex(xs[i], ys[i]);
      this.items[this.cursor[c]++] = i;
    }
  }

  /**
   * (x, y) 중심 반경 r 안에 있을 수 있는 후보들을 out에 채운다.
   * 셀 단위 근사라 실제 거리 검사는 호출자가 한다. 반환값은 후보 수.
   */
  /**
   * `(x, y)` 둘레 `r`이 걸치는 칸 범위를 `out`에 [minCx, maxCx, minCy, maxCy]로. 목록을 베끼지 않고 칸을 직접 돌며
   * 일찍 멈추려는 쪽(`World.crowdAround`)이 쓴다 — `query`는 후보를 전부 베낀 뒤에야 거를 수 있다.
   */
  cellsAround(x: number, y: number, r: number, out: Int32Array): void {
    out[0] = Math.max(0, Math.floor((x - r) / this.cellSize));
    out[1] = Math.min(this.cols - 1, Math.floor((x + r) / this.cellSize));
    out[2] = Math.max(0, Math.floor((y - r) / this.cellSize));
    out[3] = Math.min(this.rows - 1, Math.floor((y + r) / this.cellSize));
  }

  /** 칸 `c`의 항목 구간 [cellStart, cellEnd). */
  cellStart(c: number): number {
    return this.starts[c];
  }

  cellEnd(c: number): number {
    return this.starts[c] + this.counts[c];
  }

  itemAt(k: number): number {
    return this.items[k];
  }

  query(x: number, y: number, r: number, out: Int32Array): number {
    const minCx = Math.max(0, Math.floor((x - r) / this.cellSize));
    const maxCx = Math.min(this.cols - 1, Math.floor((x + r) / this.cellSize));
    const minCy = Math.max(0, Math.floor((y - r) / this.cellSize));
    const maxCy = Math.min(this.rows - 1, Math.floor((y + r) / this.cellSize));
    let n = 0;
    const cap = out.length;
    for (let cy = minCy; cy <= maxCy; cy++) {
      const row = cy * this.cols;
      for (let cx = minCx; cx <= maxCx; cx++) {
        const c = row + cx;
        const end = this.starts[c] + this.counts[c];
        for (let k = this.starts[c]; k < end; k++) {
          if (n >= cap) return n;
          out[n++] = this.items[k];
        }
      }
    }
    return n;
  }
}
